import {
  startTransition,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type OpsMonitoringFleetPilotLead,
  type OpsMonitoringGrievanceCase,
  type OpsMonitoringNotificationIssue,
  type OpsMonitoringPostProcessIssue,
  type OpsMonitoringRoutingGap,
  type OpsMonitoringSirenOnboardingRecord,
  type OpsMonitoringSirenRegistryRecord,
  type OpsSirenRoutingPreview,
  type OpsMonitoringSirenActivation,
  type OpsMonitoringSnapshot,
  type OperatorOrganizationRecord,
} from "@/lib/api/client"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canAccessOpsMonitoringWorkspace,
  canManagePortalOrganizations,
  resolveOperatorSession,
  titleizeToken,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

const INPUT_CLASS =
  "w-full rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:border-primary/50"
const EMPTY_PILOT_LEADS: OpsMonitoringFleetPilotLead[] = []
const EMPTY_SIREN_REGISTRY: OpsMonitoringSirenRegistryRecord[] = []
const EMPTY_SIREN_ONBOARDING: OpsMonitoringSirenOnboardingRecord[] = []

export interface OpsMonitoringSearch {
  organizationId?: string
  pilotLeadId?: string
}

interface OpsMonitoringWorkspaceProps {
  onSearchChange: (next: Partial<OpsMonitoringSearch>) => void
  search: OpsMonitoringSearch
}

interface OpsMonitoringBundle {
  organizations: OperatorOrganizationRecord[]
  session: OperatorResolvedSession | null
  snapshot: OpsMonitoringSnapshot
}

interface SirenRoutingPreviewFormState {
  gpsLatitude: string
  gpsLongitude: string
  plantationId: string
  zoneName: string
}

function createEmptyOpsSnapshot(organizationId?: string): OpsMonitoringSnapshot {
  return {
    generatedAt: new Date().toISOString(),
    fleetPilotLeads: [],
    grievanceCases: [],
    notifications: [],
    postProcessJobs: [],
    routingGaps: [],
    sirenActivations: [],
    sirenOnboarding: [],
    sirenRegistry: [],
    scope: {
      actorType: "public",
      organizationId: organizationId || null,
      organizationName: null,
      role: "public",
      visibility: organizationId ? "organization" : "global",
    },
    success: true,
    summary: {
      activeAlerts: 0,
      notifications: {
        deadLetterCount: 0,
        failedCount: 0,
        retryableCount: 0,
        totalProblems: 0,
      },
      postProcessJobs: {
        agedCount: 0,
        retryableCount: 0,
        stalledCount: 0,
        totalProblems: 0,
      },
      routing: {
        criticalGapCount: 0,
        gapCount: 0,
        organizationGapCount: 0,
      },
      sirens: {
        awaitingFirstCheckInCount: 0,
        failedCount: 0,
        pendingCount: 0,
        recentCount: 0,
        recentConfirmedCount: 0,
        staleOnboardingCount: 0,
      },
    },
  }
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatScope(snapshot: OpsMonitoringSnapshot) {
  if (snapshot.scope?.visibility === "global") {
    return "All organizations"
  }

  return snapshot.scope?.organizationName || "Scoped organization view"
}

function canAcknowledgeSiren(issue: OpsMonitoringSirenActivation) {
  const status = String(issue.status || "").toLowerCase()
  return (
    !issue.success &&
    issue.operatorStatus !== "acknowledged" &&
    !["pending", "queued", "submitted"].includes(status)
  )
}

function canRetrySiren(issue: OpsMonitoringSirenActivation) {
  const status = String(issue.status || "").toLowerCase()
  return !issue.success && !["pending", "queued", "submitted"].includes(status)
}

function formatProvisioningStatus(value: string | null | undefined) {
  switch (value) {
    case "confirmed":
      return "Confirmed"
    case "awaiting_first_checkin":
      return "Awaiting first check-in"
    default:
      return titleizeToken(value || "pending")
  }
}

function formatOnboardingState(value: string | null | undefined) {
  switch (value) {
    case "confirmed_recently":
      return "First heartbeat confirmed"
    case "awaiting_first_checkin":
      return "Awaiting first heartbeat"
    case "stale":
      return "Stale onboarding"
    case "confirmed":
      return "Provisioning confirmed"
    default:
      return titleizeToken(value || "pending")
  }
}

async function loadOpsMonitoringBundle(
  search: OpsMonitoringSearch
): Promise<OpsMonitoringBundle> {
  let session: OperatorResolvedSession | null = null
  try {
    const resolvedSession = await resolveOperatorSession()
    if (canAccessOpsMonitoringWorkspace(resolvedSession)) {
      session = resolvedSession
    }
  } catch {
    session = null
  }

  const isPortalAdmin = Boolean(session && canManagePortalOrganizations(session))
  const organizationId = isPortalAdmin ? search.organizationId : undefined

  const [snapshotResult, organizationsResult] = await Promise.allSettled([
    apiClient.getOpsMonitoring({
      limit: 18,
      organizationId,
    }),
    isPortalAdmin ? apiClient.getOrganizations() : Promise.resolve(null),
  ])

  const snapshot =
    snapshotResult.status === "fulfilled"
      ? snapshotResult.value
      : createEmptyOpsSnapshot(organizationId)

  return {
    organizations:
      organizationsResult.status === "fulfilled"
        ? (organizationsResult.value?.data ?? [])
        : [],
    session,
    snapshot,
  }
}

export function OpsMonitoringWorkspace({
  onSearchChange,
  search,
}: OpsMonitoringWorkspaceProps) {
  const [bundle, setBundle] = useState<OpsMonitoringBundle | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [activeAction, setActiveAction] = useState<{
    activationId: string
    type: "acknowledge" | "retry"
  } | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewForm, setPreviewForm] = useState<SirenRoutingPreviewFormState>({
    gpsLatitude: "",
    gpsLongitude: "",
    plantationId: "",
    zoneName: "",
  })
  const [previewResult, setPreviewResult] = useState<OpsSirenRoutingPreview | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  async function refreshBundle() {
    const nextBundle = await loadOpsMonitoringBundle({
      organizationId: search.organizationId,
    })
    startTransition(() => {
      setBundle(nextBundle)
      setIsLoading(false)
    })
  }

  const fleetPilotLeads = bundle?.snapshot.fleetPilotLeads ?? EMPTY_PILOT_LEADS
  const sirenRegistry = bundle?.snapshot.sirenRegistry ?? EMPTY_SIREN_REGISTRY
  const sirenOnboarding = bundle?.snapshot.sirenOnboarding ?? EMPTY_SIREN_ONBOARDING
  const selectedPilotLead = useMemo(
    () => fleetPilotLeads.find((item) => item.id === search.pilotLeadId) ?? null,
    [fleetPilotLeads, search.pilotLeadId]
  )
  const staleOnboardingItems = useMemo(
    () => sirenOnboarding.filter((item) => item.onboardingState === "stale"),
    [sirenOnboarding]
  )
  const recentConfirmedOnboardingItems = useMemo(
    () => sirenOnboarding.filter((item) => item.onboardingState === "confirmed_recently"),
    [sirenOnboarding]
  )
  const plantationOptions = useMemo(() => {
    const items = new Map<string, { plantationId: string; plantationName: string }>()

    for (const siren of sirenRegistry) {
      if (!siren.plantationId) {
        continue
      }

      items.set(siren.plantationId, {
        plantationId: siren.plantationId,
        plantationName: siren.plantationName || siren.plantationId,
      })
    }

    return Array.from(items.values()).sort((left, right) =>
      left.plantationName.localeCompare(right.plantationName)
    )
  }, [sirenRegistry])

  useEffect(() => {
    if (!plantationOptions.length) {
      return
    }

    setPreviewForm((current) => {
      if (current.plantationId) {
        return current
      }

      return {
        ...current,
        plantationId: plantationOptions[0].plantationId,
      }
    })
  }, [plantationOptions])

  async function handleLoadRoutingPreview() {
    if (!previewForm.plantationId) {
      return
    }

    startTransition(() => {
      setIsPreviewLoading(true)
      setPreviewError(null)
    })

    try {
      const response = await apiClient.getOpsSirenRoutingPreview({
        gpsLatitude: previewForm.gpsLatitude.trim()
          ? Number.parseFloat(previewForm.gpsLatitude)
          : null,
        gpsLongitude: previewForm.gpsLongitude.trim()
          ? Number.parseFloat(previewForm.gpsLongitude)
          : null,
        organizationId: search.organizationId,
        plantationId: previewForm.plantationId,
        zoneName: previewForm.zoneName.trim() || null,
      })

      startTransition(() => {
        setPreviewResult(response.data)
      })
    } catch (error) {
      startTransition(() => {
        setPreviewError(
          error instanceof Error
            ? error.message
            : "Failed to load the siren routing preview."
        )
      })
    } finally {
      startTransition(() => {
        setIsPreviewLoading(false)
      })
    }
  }

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadOpsMonitoringBundle({
          organizationId: search.organizationId,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the operations workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search.organizationId])

  async function handleAcknowledgeSiren(activationId: string) {
    startTransition(() => {
      setActiveAction({ activationId, type: "acknowledge" })
      setActionMessage(null)
      setErrorMessage(null)
    })

    try {
      await apiClient.acknowledgeOpsSirenActivation(activationId)
      await refreshBundle()
      startTransition(() => {
        setActionMessage("Marked the siren attempt as reviewed.")
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to acknowledge the siren attempt."
        )
      })
    } finally {
      startTransition(() => {
        setActiveAction(null)
      })
    }
  }

  async function handleRetrySiren(activationId: string) {
    startTransition(() => {
      setActiveAction({ activationId, type: "retry" })
      setActionMessage(null)
      setErrorMessage(null)
    })

    try {
      await apiClient.retryOpsSirenActivation(activationId)
      await refreshBundle()
      startTransition(() => {
        setActionMessage("Queued a fresh siren retry attempt.")
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to retry the siren attempt."
        )
      })
    } finally {
      startTransition(() => {
        setActiveAction(null)
      })
    }
  }

  const canScopeOrganizations = Boolean(
    bundle?.session && canManagePortalOrganizations(bundle.session)
  )
  return (
    <>
      {actionMessage ? (
        <OperatorStatusMessage tone="info">{actionMessage}</OperatorStatusMessage>
      ) : null}
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <OpsMonitoringLoadingState /> : null}

      {!isLoading && !bundle && !errorMessage ? (
        <OperatorPanel
          title="Operations access restricted"
          description="This page is only available to sessions with operations monitoring access."
        >
          <OperatorStatusMessage tone="danger">
            Sign in again with an admin account to open the operations monitor.
          </OperatorStatusMessage>
        </OperatorPanel>
      ) : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Monitoring scope"
            description="Scope the snapshot to one organization when your account allows it."
          >
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Organization scope</span>
                <select
                  aria-label="Organization scope"
                  className={INPUT_CLASS}
                  disabled={!canScopeOrganizations}
                  value={search.organizationId || ""}
                  onChange={(event) =>
                    onSearchChange({
                      organizationId: event.target.value || undefined,
                      pilotLeadId: undefined,
                    })
                  }
                >
                  <option value="">All visible organizations</option>
                  {bundle.organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="rounded-2xl border border-border/70 bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                {formatScope(bundle.snapshot)} · Snapshot{" "}
                {formatDateTime(bundle.snapshot.generatedAt)}
              </div>
            </div>
          </OperatorPanel>

          {recentConfirmedOnboardingItems.length ? (
            <OperatorStatusMessage tone="info">
              First heartbeat confirmed for{" "}
              {recentConfirmedOnboardingItems
                .map((item) => item.displayName || item.id)
                .join(", ")}
              . Review the onboarding history below if the installer needs proof of activation.
            </OperatorStatusMessage>
          ) : null}

          {staleOnboardingItems.length ? (
            <OperatorStatusMessage tone="warning">
              {staleOnboardingItems.length} provisioned siren
              {staleOnboardingItems.length === 1 ? " is" : "s are"} still waiting for a
              first heartbeat past the onboarding window.
            </OperatorStatusMessage>
          ) : null}

          <OperatorPanel
            title="Siren routing preview"
            description="Preview which sirens would be selected for a plantation panic event using the current health, geography, and routing policy."
          >
            <div className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-4">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Plantation</span>
                  <select
                    aria-label="Preview plantation"
                    className={INPUT_CLASS}
                    value={previewForm.plantationId}
                    onChange={(event) =>
                      setPreviewForm((current) => ({
                        ...current,
                        plantationId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select plantation</option>
                    {plantationOptions.map((option) => (
                      <option key={option.plantationId} value={option.plantationId}>
                        {option.plantationName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Zone</span>
                  <input
                    aria-label="Preview zone"
                    className={INPUT_CLASS}
                    placeholder="North Block"
                    value={previewForm.zoneName}
                    onChange={(event) =>
                      setPreviewForm((current) => ({
                        ...current,
                        zoneName: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Latitude</span>
                  <input
                    aria-label="Preview latitude"
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    placeholder="12.9716"
                    value={previewForm.gpsLatitude}
                    onChange={(event) =>
                      setPreviewForm((current) => ({
                        ...current,
                        gpsLatitude: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium">Longitude</span>
                  <input
                    aria-label="Preview longitude"
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    placeholder="77.5946"
                    value={previewForm.gpsLongitude}
                    onChange={(event) =>
                      setPreviewForm((current) => ({
                        ...current,
                        gpsLongitude: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={!previewForm.plantationId || isPreviewLoading}
                  type="button"
                  onClick={handleLoadRoutingPreview}
                >
                  {isPreviewLoading ? "Loading preview…" : "Preview siren routing"}
                </Button>
              </div>

              {previewError ? (
                <OperatorStatusMessage tone="danger">{previewError}</OperatorStatusMessage>
              ) : null}

              {previewResult ? (
                <div className="grid gap-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-sm text-muted-foreground">
                    {[
                      previewResult.plantationName || previewResult.plantationId,
                      `Route ${titleizeToken(previewResult.routeScope || "zone_only")}`,
                      `Targets ${previewResult.targetCount || 0}/${previewResult.sirenCount || 0}`,
                      previewResult.targetZoneName
                        ? `Zone ${previewResult.targetZoneName}`
                        : "No zone filter",
                    ].join(" · ")}
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="grid gap-3">
                      <p className="text-sm font-medium">Selected targets</p>
                      {previewResult.targets.length ? (
                        previewResult.targets.map((item) => (
                          <SirenPreviewCard issue={item} key={`target-${item.id}`} />
                        ))
                      ) : (
                        <OperatorEmptyState
                          title="No sirens would be triggered."
                          message="The current health and routing inputs do not produce a target."
                        />
                      )}
                    </div>
                    <div className="grid gap-3">
                      <p className="text-sm font-medium">Other evaluated sirens</p>
                      {previewResult.evaluatedSirens
                        .filter((item) => !item.selected)
                        .map((item) => (
                          <SirenPreviewCard issue={item} key={`evaluated-${item.id}`} />
                        ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="Fleet pilot lead detail"
            description={
              selectedPilotLead
                ? "Review the full lead profile, rollout goals, and notification state before follow-up."
                : "Open a lead from the fleet pilot queue to inspect the full submission."
            }
          >
            {selectedPilotLead ? (
              <FleetPilotLeadDetail
                issue={selectedPilotLead}
                onClear={() => {
                  onSearchChange({
                    pilotLeadId: undefined,
                  })
                }}
              />
            ) : search.pilotLeadId ? (
              <div className="grid gap-4">
                <OperatorStatusMessage tone="warning">
                  The selected lead is outside the active monitoring scope.
                </OperatorStatusMessage>
                <div>
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      onSearchChange({
                        pilotLeadId: undefined,
                      })
                    }}
                  >
                    Clear lead selection
                  </Button>
                </div>
              </div>
            ) : (
              <OperatorEmptyState
                title="No lead selected."
                message="Choose a fleet pilot lead from the queue to review contact details, rollout goals, and submission state here."
              />
            )}
          </OperatorPanel>

          <div className="grid gap-5 xl:grid-cols-3">
            <MonitoringListPanel
              description="Qualified fleet pilot leads submitted from the public Fleet Safety funnel."
              items={fleetPilotLeads}
              renderItem={(item) => (
                <FleetPilotLeadCard
                  issue={item}
                  isSelected={search.pilotLeadId === item.id}
                  key={item.id}
                  onSelect={(leadId) => {
                    onSearchChange({
                      pilotLeadId: leadId,
                    })
                  }}
                />
              )}
              title="Fleet pilot leads"
            />
            <MonitoringListPanel
              description="Tracked grievance, correction, withdrawal, deletion, and guardian approval help requests that need operator follow-up."
              items={bundle.snapshot.grievanceCases}
              renderItem={(item) => <GrievanceCaseCard issue={item} key={item.id} />}
              title="Grievance queue"
            />
            <MonitoringListPanel
              description="Failed, retryable, and dead-letter notification deliveries from the current ops snapshot."
              items={bundle.snapshot.notifications}
              renderItem={(item) => <NotificationIssueCard issue={item} key={item.id} />}
              title="Problem deliveries"
            />
            <MonitoringListPanel
              description="Retryable, stalled, and aged post-process jobs that need operational attention."
              items={bundle.snapshot.postProcessJobs}
              renderItem={(item) => <PostProcessIssueCard issue={item} key={item.id} />}
              title="Post-process jobs"
            />
            <MonitoringListPanel
              description="Active jurisdictions whose organization recipient coverage is incomplete or absent."
              items={bundle.snapshot.routingGaps}
              renderItem={(item) => <RoutingGapCard issue={item} key={item.id} />}
              title="Routing gaps"
            />
            <MonitoringListPanel
              description="Provisioned sirens that are still awaiting their first heartbeat, plus the most recent confirmed installer check-ins."
              items={bundle.snapshot.sirenOnboarding ?? []}
              renderItem={(item) => <SirenOnboardingCard issue={item} key={item.id} />}
              title="Installer onboarding"
            />
            <MonitoringListPanel
              description="Current plantation siren health, heartbeat freshness, and scheduled maintenance windows across the active scope."
              items={bundle.snapshot.sirenRegistry ?? []}
              renderItem={(item) => <SirenRegistryCard issue={item} key={item.id} />}
              title="Siren health"
            />
            <MonitoringListPanel
              description="Recent panic-triggered siren activation attempts, including queued, delivered, and failed device alerts."
              items={bundle.snapshot.sirenActivations}
              renderItem={(item) => (
                <SirenActivationCard
                  issue={item}
                  isAcknowledging={
                    activeAction?.activationId === item.id &&
                    activeAction.type === "acknowledge"
                  }
                  isRetrying={
                    activeAction?.activationId === item.id &&
                    activeAction.type === "retry"
                  }
                  key={item.id}
                  onAcknowledge={handleAcknowledgeSiren}
                  onRetry={handleRetrySiren}
                />
              )}
              title="Siren activations"
            />
          </div>
        </>
      ) : null}
    </>
  )
}

function OpsMonitoringLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Monitoring scope"
        description="Scope the snapshot to one organization when your account allows it."
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <Skeleton className="h-11 w-full rounded-2xl" />
          <Skeleton className="h-11 w-56 rounded-2xl" />
        </div>
      </OperatorPanel>

      <OperatorPanel
        title="Fleet pilot lead detail"
        description="Loading the selected fleet pilot submission detail."
      >
        <div className="grid gap-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-5 w-64" />
          <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: 6 }).map((_, fieldIndex) => (
              <div
                key={`ops-monitor-lead-detail-field-${fieldIndex}`}
                className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <Skeleton className="h-3 w-24" />
                <Skeleton className="mt-3 h-4 w-5/6" />
              </div>
            ))}
          </div>
        </div>
      </OperatorPanel>

      <div className="grid gap-5 xl:grid-cols-3">
        {Array.from({ length: 8 }).map((_, panelIndex) => (
          <OperatorPanel
            key={`ops-monitor-loading-panel-${panelIndex}`}
            title={[
              "Fleet pilot leads",
              "Grievance queue",
              "Problem deliveries",
              "Post-process jobs",
              "Routing gaps",
              "Installer onboarding",
              "Siren health",
              "Siren activations",
            ][panelIndex]}
            description="Loading the latest operator snapshot for this monitoring panel."
          >
            <div className="grid gap-3">
              {Array.from({ length: 3 }).map((_, rowIndex) => (
                <div
                  key={`ops-monitor-loading-row-${panelIndex}-${rowIndex}`}
                  className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                  <Skeleton className="mt-3 h-4 w-5/6" />
                  <Skeleton className="mt-2 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-2/3" />
                </div>
              ))}
            </div>
          </OperatorPanel>
        ))}
      </div>
    </>
  )
}

function SirenActivationCard({
  issue,
  isAcknowledging = false,
  isRetrying = false,
  onAcknowledge,
  onRetry,
}: {
  issue: OpsMonitoringSirenActivation
  isAcknowledging?: boolean
  isRetrying?: boolean
  onAcknowledge: (activationId: string) => void
  onRetry: (activationId: string) => void
}) {
  const statusToneClass =
    issue.operatorStatus === "acknowledged"
      ? "rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
      : issue.success
      ? "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
      : !issue.success && !["pending", "queued", "submitted"].includes(String(issue.status || "").toLowerCase())
        ? "rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
        : "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"

  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className={statusToneClass}>
          {issue.operatorStatus === "acknowledged"
            ? "Acknowledged"
            : titleizeToken(issue.status || (issue.success ? "sent" : "pending"))}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {issue.displayName || "Unnamed siren"}
        {issue.incidentId ? ` · Incident #${issue.incidentId}` : ""}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {[issue.plantationName || issue.plantationId, issue.zoneName || "Central coverage"]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Recipient {issue.recipientPhone || "Unknown"} · route{" "}
        {titleizeToken(issue.routeScope || "zone_only")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Attempted {formatDateTime(issue.attemptedAt || issue.createdAt)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Health {titleizeToken(issue.sirenHealthState || "unknown")}
        {issue.lastHeartbeatAt
          ? ` · heartbeat ${formatDateTime(issue.lastHeartbeatAt)}`
          : issue.lastSeenAt
            ? ` · seen ${formatDateTime(issue.lastSeenAt)}`
            : ""}
        {typeof issue.consecutiveFailureCount === "number"
          ? ` · failures ${issue.consecutiveFailureCount}`
          : ""}
      </p>
      {issue.manualRetryCount ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Manual retries {issue.manualRetryCount}
          {issue.lastRetriedAt ? ` · last ${formatDateTime(issue.lastRetriedAt)}` : ""}
          {issue.lastRetriedByName ? ` by ${issue.lastRetriedByName}` : ""}
        </p>
      ) : null}
      {issue.acknowledgedAt ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Acknowledged {formatDateTime(issue.acknowledgedAt)}
          {issue.acknowledgedByName ? ` by ${issue.acknowledgedByName}` : ""}
        </p>
      ) : null}
      {issue.errorMessage ? (
        <p className="mt-1 text-sm text-destructive">{issue.errorMessage}</p>
      ) : null}
      {canAcknowledgeSiren(issue) || canRetrySiren(issue) ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {canAcknowledgeSiren(issue) ? (
            <Button
              disabled={isAcknowledging || isRetrying}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => onAcknowledge(issue.id)}
            >
              {isAcknowledging ? "Acknowledging…" : "Acknowledge"}
            </Button>
          ) : null}
          {canRetrySiren(issue) ? (
            <Button
              disabled={isRetrying || isAcknowledging}
              size="sm"
              type="button"
              onClick={() => onRetry(issue.id)}
            >
              {isRetrying ? "Retrying…" : "Retry now"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function SirenOnboardingCard({
  issue,
}: {
  issue: OpsMonitoringSirenOnboardingRecord
}) {
  const statusToneClass =
    issue.onboardingState === "confirmed_recently"
      ? "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
      : issue.onboardingState === "stale"
        ? "rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
        : "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"

  const latestHistory = issue.maintenanceHistory?.filter((event) =>
    [
      "installer_packet_generated",
      "installer_packet_regenerated",
      "first_device_check_in_confirmed",
    ].includes(String(event.eventType || ""))
  ) ?? []

  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className={statusToneClass}>
          {formatOnboardingState(issue.onboardingState)}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {issue.displayName || "Unnamed siren"}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {[issue.plantationName || issue.plantationId, issue.zoneName || "Central coverage"]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          `Provisioning ${formatProvisioningStatus(issue.provisioningStatus)}`,
          issue.heartbeatProvisionedAt
            ? `Packet ${formatDateTime(issue.heartbeatProvisionedAt)}`
            : null,
          issue.heartbeatProvisionedByName || null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {issue.firstDeviceCheckInAt
          ? `First heartbeat ${formatDateTime(issue.firstDeviceCheckInAt)}`
          : issue.minutesSinceProvisioned !== null &&
              issue.minutesSinceProvisioned !== undefined
            ? `Awaiting first heartbeat for ${issue.minutesSinceProvisioned} minutes`
            : "Waiting for the first device heartbeat"}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          `Health ${titleizeToken(issue.healthState || "unknown")}`,
          issue.locationLabel || null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {latestHistory.length ? (
        <div className="mt-3 grid gap-2">
          {latestHistory.slice(0, 2).map((event) => (
            <div
              key={event.id}
              className="rounded-2xl bg-background/75 px-3 py-2 text-sm text-muted-foreground"
            >
              <span className="font-medium text-foreground">
                {titleizeToken(event.eventType || "event")}
              </span>
              <span>{` · ${formatDateTime(event.createdAt)}`}</span>
              {event.note ? <p className="mt-1">{event.note}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function SirenRegistryCard({
  issue,
}: {
  issue: OpsMonitoringSirenRegistryRecord
}) {
  const statusToneClass =
    issue.healthState === "healthy"
      ? "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
      : ["maintenance", "disabled_failure", "disabled_manual"].includes(
            String(issue.healthState || "")
          )
        ? "rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
        : "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"

  const locationSummary =
    [
      issue.locationLabel || null,
      issue.latitude !== null && issue.latitude !== undefined
        ? `${issue.latitude}, ${issue.longitude ?? "?"}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ") || "Location not set"

  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className={statusToneClass}>
          {titleizeToken(issue.healthState || "unknown")}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {issue.displayName || "Unnamed siren"}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {[issue.plantationName || issue.plantationId, issue.zoneName || "Central coverage"]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          issue.isActive ? "Active" : "Disabled",
          issue.triggerOnPanic ? "Triggers on panic" : "Ignored for panic",
          `Order ${issue.escalationOrder ?? 0}`,
        ].join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          `Provisioning ${formatProvisioningStatus(issue.provisioningStatus)}`,
          issue.heartbeatProvisionedAt
            ? `Packet ${formatDateTime(issue.heartbeatProvisionedAt)}`
            : null,
          issue.firstDeviceCheckInAt
            ? `First heartbeat ${formatDateTime(issue.firstDeviceCheckInAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          locationSummary,
          issue.lastHeartbeatAt
            ? `Heartbeat ${formatDateTime(issue.lastHeartbeatAt)}`
            : issue.lastSeenAt
              ? `Seen ${formatDateTime(issue.lastSeenAt)}`
              : "No heartbeat yet",
          issue.lastHeartbeatSource
            ? `Source ${titleizeToken(issue.lastHeartbeatSource)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {typeof issue.consecutiveFailureCount === "number" ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Failures {issue.consecutiveFailureCount}
          {issue.failureDisabledReason ? ` · ${issue.failureDisabledReason}` : ""}
        </p>
      ) : null}
      {issue.scheduledMaintenanceStartAt || issue.scheduledMaintenanceEndAt ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Scheduled {formatDateTime(issue.scheduledMaintenanceStartAt)} →{" "}
          {formatDateTime(issue.scheduledMaintenanceEndAt)}
          {issue.scheduledMaintenanceNote
            ? ` · ${issue.scheduledMaintenanceNote}`
            : ""}
        </p>
      ) : null}
      {issue.maintenanceHistory?.length ? (
        <div className="mt-3 grid gap-2">
          {issue.maintenanceHistory.slice(0, 2).map((event) => (
            <div
              key={event.id}
              className="rounded-2xl bg-background/75 px-3 py-2 text-sm text-muted-foreground"
            >
              <span className="font-medium text-foreground">
                {titleizeToken(event.eventType || "event")}
              </span>
              <span>{` · ${formatDateTime(event.createdAt)}`}</span>
              {event.note ? <p className="mt-1">{event.note}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function SirenPreviewCard({
  issue,
}: {
  issue: {
    displayName?: string | null
    distanceMeters?: number | null
    escalationOrder?: number | null
    healthState?: string | null
    id: string
    locationLabel?: string | null
    selected?: boolean | null
    selectionReason?: string | null
    zoneName?: string | null
  }
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.displayName || "Unnamed siren"}
        </span>
        <span
          className={
            issue.selected
              ? "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
              : "rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
          }
        >
          {issue.selected ? "Selected" : titleizeToken(issue.selectionReason || "evaluated")}
        </span>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {[issue.zoneName || "Central coverage", issue.locationLabel || null]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {[
          `Health ${titleizeToken(issue.healthState || "unknown")}`,
          `Order ${issue.escalationOrder ?? 0}`,
          typeof issue.distanceMeters === "number"
            ? `${issue.distanceMeters}m from incident`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  )
}

function FleetPilotLeadCard({
  issue,
  isSelected = false,
  onSelect,
}: {
  issue: OpsMonitoringFleetPilotLead
  isSelected?: boolean
  onSelect: (leadId: string) => void
}) {
  return (
    <div
      className={
        isSelected
          ? "rounded-3xl border border-primary/35 bg-primary/5 px-4 py-4"
          : "rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-medium">
          {issue.companyName || "Unknown company"}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {titleizeToken(issue.status || "new")}
          </span>
          <Button
            size="sm"
            type="button"
            variant={isSelected ? "secondary" : "outline"}
            onClick={() => onSelect(issue.id)}
          >
            {isSelected ? "Selected" : "Open detail"}
          </Button>
        </div>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {titleizeToken(issue.fleetSegment || "fleet")}
        {issue.fleetSizeBand ? ` · ${issue.fleetSizeBand}` : ""}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Contact {issue.contactName || "Not provided"} · {issue.workEmail || "No email"} ·{" "}
        {issue.phone || "No phone"}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Scope{" "}
        {[issue.city || null, issue.operatingRegion || null].filter(Boolean).join(" · ")
          || "Not provided"}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Vehicles{" "}
        {typeof issue.estimatedVehicleCount === "number"
          ? issue.estimatedVehicleCount
          : "Not provided"}{" "}
        · submitted {formatDateTime(issue.createdAt)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Notification {titleizeToken(issue.notificationStatus || "pending")}
        {issue.notificationError ? ` (${issue.notificationError})` : ""}
      </p>
    </div>
  )
}

function FleetPilotLeadDetail({
  issue,
  onClear,
}: {
  issue: OpsMonitoringFleetPilotLead
  onClear: () => void
}) {
  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h3 className="text-lg font-semibold">
            {issue.companyName || "Unknown company"}
          </h3>
          <p className="text-sm leading-6 text-muted-foreground">
            {[
              issue.contactName || "Contact not provided",
              issue.workEmail || "No email",
              issue.phone || "No phone",
            ].join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {titleizeToken(issue.status || "new")}
          </span>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            Notification {titleizeToken(issue.notificationStatus || "pending")}
          </span>
          <Button size="sm" type="button" variant="outline" onClick={onClear}>
            Clear selection
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <FleetPilotLeadDetailField label="Company" value={issue.companyName} />
        <FleetPilotLeadDetailField label="Contact" value={issue.contactName} />
        <FleetPilotLeadDetailField label="Email" value={issue.workEmail} />
        <FleetPilotLeadDetailField label="Phone" value={issue.phone} />
        <FleetPilotLeadDetailField
          label="Fleet segment"
          value={issue.fleetSegment ? titleizeToken(issue.fleetSegment) : null}
        />
        <FleetPilotLeadDetailField label="Fleet size" value={issue.fleetSizeBand} />
        <FleetPilotLeadDetailField label="City" value={issue.city} />
        <FleetPilotLeadDetailField label="Region" value={issue.operatingRegion} />
        <FleetPilotLeadDetailField
          label="Vehicle count"
          value={
            typeof issue.estimatedVehicleCount === "number"
              ? String(issue.estimatedVehicleCount)
              : null
          }
        />
        <FleetPilotLeadDetailField
          label="Status"
          value={issue.status ? titleizeToken(issue.status) : null}
        />
        <FleetPilotLeadDetailField
          label="Notification state"
          value={issue.notificationStatus ? titleizeToken(issue.notificationStatus) : "Pending"}
        />
        <FleetPilotLeadDetailField
          label="Submitted"
          value={formatDateTime(issue.createdAt)}
        />
        <FleetPilotLeadDetailField
          label="Reference ID"
          monospace
          value={issue.id}
        />
        <FleetPilotLeadDetailField
          label="Source surface"
          value={issue.sourceSurface ? titleizeToken(issue.sourceSurface) : null}
        />
        <FleetPilotLeadDetailField
          label="Assigned organization"
          value={issue.organizationName}
        />
        <FleetPilotLeadDetailField
          label="Notification updated"
          value={formatDateTime(issue.notifiedAt || issue.updatedAt)}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <FleetPilotLeadDetailBlock
          label="Safety stack"
          value={issue.currentSafetyStack}
        />
        <FleetPilotLeadDetailBlock
          label="Pilot goals"
          value={issue.pilotGoals}
        />
        <FleetPilotLeadDetailBlock
          label="Rollout timeline"
          value={issue.rolloutTimeline}
        />
        <FleetPilotLeadDetailBlock
          label="Preferred demo window"
          value={issue.preferredDemoWindow}
        />
      </div>

      {issue.notificationError ? (
        <OperatorStatusMessage tone="warning">
          Notification error: {issue.notificationError}
        </OperatorStatusMessage>
      ) : null}
    </div>
  )
}

function FleetPilotLeadDetailField({
  label,
  monospace = false,
  value,
}: {
  label: string
  monospace?: boolean
  value: string | null | undefined
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {label}
      </div>
      <div
        className={
          monospace
            ? "mt-3 break-all font-mono text-xs leading-6 text-foreground"
            : "mt-3 text-sm leading-6 text-foreground"
        }
      >
        {value || "Not provided"}
      </div>
    </div>
  )
}

function FleetPilotLeadDetailBlock({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {label}
      </div>
      <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground">
        {value || "Not provided"}
      </div>
    </div>
  )
}

function GrievanceCaseCard({
  issue,
}: {
  issue: OpsMonitoringGrievanceCase
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span
          className={
            issue.isOverdue
              ? "rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive"
              : "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
          }
        >
          {issue.isOverdue ? "Overdue" : titleizeToken(issue.status || "open")}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {titleizeToken(issue.caseType || "grievance")}
        {issue.tag ? ` · ${issue.tag}` : ""}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Requester {issue.requesterName || "Not provided"} · assignee{" "}
        {issue.assignee || "Unassigned"}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Created {formatDateTime(issue.createdAt)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        SLA due {formatDateTime(issue.slaDueAt)} · compliance{" "}
        {issue.registrationComplianceId || "Not linked"}
      </p>
    </div>
  )
}

function MonitoringListPanel<TItem>({
  description,
  items,
  renderItem,
  title,
}: {
  description: string
  items: TItem[]
  renderItem: (item: TItem) => ReactNode
  title: string
}) {
  return (
    <OperatorPanel title={title} description={description}>
      {items.length ? (
        <div className="grid gap-3">{items.map(renderItem)}</div>
      ) : (
        <OperatorEmptyState
          title={`No ${title.toLowerCase()} in the current snapshot.`}
          message="The current scope is clear for this specific monitoring panel."
        />
      )}
    </OperatorPanel>
  )
}

function NotificationIssueCard({
  issue,
}: {
  issue: OpsMonitoringNotificationIssue
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          {titleizeToken(issue.status)}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {titleizeToken(issue.channel)} notification
        {issue.incidentId ? ` · Incident #${issue.incidentId}` : ""}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {issue.errorMessage || issue.detailedStatus || "Provider delivery failed without a structured error payload."}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Recipient {issue.recipient || "Unknown"} · attempts {issue.attemptCount || 0}/
        {issue.maxAttempts || 0}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Next retry {formatDateTime(issue.nextAttemptAt)}
      </p>
    </div>
  )
}

function PostProcessIssueCard({
  issue,
}: {
  issue: OpsMonitoringPostProcessIssue
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className="rounded-full bg-chart-4/15 px-3 py-1 text-xs font-medium text-foreground">
          {issue.attentionLabel || titleizeToken(issue.status)}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        Post-process job · Incident #{issue.incidentId ?? "unknown"}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {issue.attentionReason || "The job requires operator attention."}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        {titleizeToken(issue.stage || issue.status)} · retries {issue.retryCount || 0}/
        {issue.maxRetries || 0}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Age {issue.ageMinutes ?? "Unknown"} min · next retry {formatDateTime(issue.nextRetryAt)}
      </p>
    </div>
  )
}

function RoutingGapCard({
  issue,
}: {
  issue: OpsMonitoringRoutingGap
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {issue.organizationName || "Unknown organization"}
        </span>
        <span className="rounded-full bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive">
          {issue.issueLabel || "Routing gap"}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold">
        {issue.jurisdictionName || "Unnamed jurisdiction"}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {issue.issueSummary || "Authority recipient coverage is incomplete for this routing target."}
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        Reachable {(issue.reachableChannels || []).map(titleizeToken).join(" · ") || "None"}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Missing {(issue.missingChannels || []).map(titleizeToken).join(" · ") || "None"}
      </p>
    </div>
  )
}
